import type { HttpLayer } from '../http.js';
import type {
  CreateLocalWorkspaceRequest,
  RestoreVersionRequest,
  SaveCheckpointRequest,
  VersionHistoryResponse,
  VersioningOpResponse,
  ViewHistoryParams,
  WorkspaceProjection,
  WorkspaceOperationHistory,
  SubscribeWorkspaceEventsRequest,
  CreateCloudWorkspaceRequest,
  BindWorkspaceRequest,
  ReassignWorkstationRequest,
  UnbindWorkspaceRequest,
  ConvertWorkspaceRequest,
  ArchiveWorkspaceRequest,
  WorkspaceLifecycleActionRequest,
  RetryWorkspaceArchiveCleanupRequest,
  CancelWorkspaceOperationRequest,
  CloudRepository,
  ExecuteFileOpRequest,
  FileOpResponse,
  FileReadResult,
  FileWriteResult,
  PodInfo,
  RepositoryStatusResponse,
  TransferWorkspaceRequest,
  SaveWorkspaceEnvironmentRequest,
} from '../types/index.js';
import { parseWorkspaceProjection, parseWorkspaceProjectionList } from '../workspace-projection.js';

const cloudFilesystemPrefix = '/v1/cloud-filesystem';

export class FileWriteConflictError extends Error {
  constructor(
    public readonly path: string,
    public readonly currentVersion: string | null,
    public readonly currentContent?: string
  ) {
    super(`writeFile(${path}) conflict: file changed since it was read`);
    this.name = 'FileWriteConflictError';
  }
}

export function parseFileReadResponse(path: string, response: FileOpResponse): FileReadResult {
  if (response.status === 'error' || response.error) {
    throw new Error(`readFile(${path}) failed: ${response.error ?? 'unknown error'}`);
  }
  if (response.status !== 'ok' && response.status !== 'success') {
    throw new Error(`readFile(${path}) returned unknown status: ${response.status}`);
  }
  if (typeof response.content !== 'string') {
    throw new Error(`readFile(${path}) returned no content`);
  }
  if (response.encoding !== 'utf8' && response.encoding !== 'base64') {
    throw new Error(`readFile(${path}) returned unknown encoding: ${String(response.encoding)}`);
  }
  return {
    content: response.content,
    encoding: response.encoding,
    version: response.version,
  };
}

export class WorkspacesResource {
  constructor(private readonly http: HttpLayer) {}

  async list(deviceId?: string): Promise<WorkspaceProjection[]> {
    return parseWorkspaceProjectionList(
      await this.http.get('/v1/workspaces', { device_id: deviceId })
    );
  }

  async getState(workspaceId: string, deviceId?: string): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.get(`/v1/workspaces/${workspaceId}/state`, { device_id: deviceId })
    );
  }

  async createLocal(params: CreateLocalWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(await this.http.post('/v1/workspaces/local', params));
  }

  /** Create a cloud workspace, optionally publishing a selected connected-device folder first. */
  async create(params: CreateCloudWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(await this.http.post('/v1/workspaces', params));
  }

  async bind(workspaceId: string, params: BindWorkspaceRequest): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/bindings`, params)
    );
  }

  /**
   * Move a local workspace onto a different computer. Unlike {@link bind} this
   * does not require the OUTGOING device to be connected — the premise is that
   * it is gone.
   */
  async reassignWorkstation(
    workspaceId: string,
    params: ReassignWorkstationRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/reassign-workstation`, params)
    );
  }

  async unbind(
    workspaceId: string,
    deviceId: string,
    params: UnbindWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(
        `/v1/workspaces/${workspaceId}/bindings/${deviceId}/actions/unbind`,
        params
      )
    );
  }

  async convert(
    workspaceId: string,
    params: ConvertWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/convert`, params)
    );
  }

  async archive(
    workspaceId: string,
    params: ArchiveWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/archive`, params)
    );
  }

  async wake(
    workspaceId: string,
    params: WorkspaceLifecycleActionRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/wake`, params)
    );
  }

  async sleep(
    workspaceId: string,
    params: WorkspaceLifecycleActionRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/sleep`, params)
    );
  }

  async retryArchiveCleanup(
    workspaceId: string,
    params: RetryWorkspaceArchiveCleanupRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/retry-cleanup`, params)
    );
  }

  /** Cancel a running cloud setup, transfer, or pre-swap conversion owned by the caller or administered by an account owner. */
  async cancelOperation(
    workspaceId: string,
    operationId: string,
    params: CancelWorkspaceOperationRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(
        `/v1/workspaces/${workspaceId}/operations/${operationId}/actions/cancel`,
        params
      )
    );
  }

  async transfer(
    workspaceId: string,
    params: TransferWorkspaceRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/actions/transfer`, params)
    );
  }

  async saveEnvironment(
    workspaceId: string,
    params: SaveWorkspaceEnvironmentRequest
  ): Promise<WorkspaceProjection> {
    return parseWorkspaceProjection(
      await this.http.post(`/v1/workspaces/${workspaceId}/environment`, params)
    );
  }

  async getOperationHistory(
    workspaceId: string,
    limit: number = 20
  ): Promise<WorkspaceOperationHistory> {
    return this.http.get(`/v1/workspaces/${workspaceId}/state/history`, { limit });
  }

  async listRepositories(): Promise<CloudRepository[]> {
    return this.http.get(`${cloudFilesystemPrefix}/repositories`);
  }

  async getRepositoryStatus(workspaceId: string): Promise<RepositoryStatusResponse> {
    return this.http.get(`${cloudFilesystemPrefix}/repositories/${workspaceId}`);
  }

  async listSandboxes(workspaceId: string): Promise<PodInfo[]> {
    return this.http.get(`${cloudFilesystemPrefix}/sandboxes/${workspaceId}`);
  }

  async executeFileOp(workspaceId: string, params: ExecuteFileOpRequest): Promise<FileOpResponse> {
    return this.http.post(`${cloudFilesystemPrefix}/file-ops/${workspaceId}/execute`, params);
  }

  async readFile(workspaceId: string, path: string): Promise<FileReadResult> {
    const response = await this.executeFileOp(workspaceId, {
      op_type: 'read',
      path,
    });
    return parseFileReadResponse(path, response);
  }

  async writeFile(
    workspaceId: string,
    path: string,
    content: string,
    expectedVersion?: string
  ): Promise<FileWriteResult> {
    const response = await this.executeFileOp(workspaceId, {
      op_type: 'write',
      path,
      content,
      ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
    });
    if (response.status === 'conflict') {
      throw new FileWriteConflictError(
        path,
        response.current_version ?? null,
        response.current_content ?? undefined
      );
    }
    if (response.status === 'error' || response.error) {
      throw new Error(`writeFile(${path}) failed: ${response.error ?? 'unknown error'}`);
    }
    if (!response.version) {
      throw new Error(`writeFile(${path}) returned no version token`);
    }
    return { version: response.version };
  }

  async viewChanges(workspaceId: string, sessionId: string): Promise<VersioningOpResponse> {
    return this.http.get(`${cloudFilesystemPrefix}/file-ops/${workspaceId}/changes`, {
      session_id: sessionId,
    });
  }

  readonly subscribeEvents = async (
    workspaceId: string,
    params: SubscribeWorkspaceEventsRequest
  ): Promise<void> => {
    return this.http.post(`/v1/workspaces/${workspaceId}/state/subscribe`, params);
  };

  readonly unsubscribeEvents = async (workspaceId: string, deviceId: string): Promise<void> => {
    return this.http.deleteWithBody(`/v1/workspaces/${workspaceId}/state/subscribe`, {
      device_id: deviceId,
    });
  };

  async saveVersion(
    workspaceId: string,
    params: SaveCheckpointRequest
  ): Promise<VersioningOpResponse> {
    return this.http.post(`/v1/workspaces/${workspaceId}/versions`, params);
  }

  async listHistory(
    workspaceId: string,
    params: ViewHistoryParams
  ): Promise<VersionHistoryResponse> {
    return this.http.get(`/v1/workspaces/${workspaceId}/versions`, params);
  }

  async restoreVersion(
    workspaceId: string,
    params: RestoreVersionRequest
  ): Promise<VersioningOpResponse> {
    return this.http.post(`/v1/workspaces/${workspaceId}/versions/restore`, params);
  }
}
